/**
 * Chart layout rules.
 *
 * Pure functions from a computed `ChartView` to a display decision. Both the
 * interactive chart (Chart.js in the browser) and the print renderer read
 * these instead of deciding independently, which is what keeps a PDF from
 * disagreeing with what the author saw on screen (spec §7.2-7.3).
 */
/** A bar category label longer than this forces horizontal bars, so the text
 *  has room to run rather than being squeezed under a vertical bar. */
export const HORIZONTAL_LABEL_CHARS = 12;
/** Above this many categories, printed value labels collide with each other. */
export const VALUE_LABEL_MAX_CATEGORIES = 12;
/** Above this many series, value labels on the same category collide. */
export const VALUE_LABEL_MAX_SERIES = 2;
/** Above this many series, end-of-line labels overlap. */
export const END_LABEL_MAX_SERIES = 4;
/** Above this many points, per-point dots clutter the line rather than aiding it. */
export const DOT_MAX_POINTS = 12;
/** Bar charts over a date dimension stay vertical regardless of label length
 *  — a date axis reads left-to-right as a timeline, and rotating it to
 *  horizontal bars would break that reading. Everything else flips to
 *  horizontal once any category label is too long to fit under a vertical bar. */
export function barOrientation(view) {
    if (view.dimensionType === 'date')
        return 'vertical';
    return view.categories.some((c) => c.length > HORIZONTAL_LABEL_CHARS) ? 'horizontal' : 'vertical';
}
/** Value labels printed on each bar. Only for bar charts, and only while
 *  there are few enough categories and series that the labels stay legible. */
export function showValueLabels(view) {
    return (view.widget.chartType === 'bar' &&
        view.categories.length <= VALUE_LABEL_MAX_CATEGORIES &&
        view.series.length <= VALUE_LABEL_MAX_SERIES);
}
/** The final value of each line, labelled at its endpoint. Only for line
 *  charts, and only while few enough series keep the endpoints apart. */
export function showEndLabels(view) {
    return view.widget.chartType === 'line' && view.series.length <= END_LABEL_MAX_SERIES;
}
/** Per-point dots on a line chart. Only while there are few enough points
 *  that dots read as data markers rather than noise. */
export function showDots(view) {
    return view.widget.chartType === 'line' && view.categories.length <= DOT_MAX_POINTS;
}
//# sourceMappingURL=chart-rules.js.map